import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TEAM_SHORT } from '../../theme/brandTheme';

/* ==========================================================
   HERO INTEL PANEL - ToBe Esports theme
   ========================================================== */

export const COLORS = {
  bg: '#0a0a0a',
  bgDark: '#050505',
  bgCard: 'rgba(12, 12, 12, 0.98)',
  bgGlass: 'rgba(255, 255, 255, 0.02)',

  orange: '#ff1026',
  orangeLight: '#ff4d5f',
  orangeDark: '#a30013',
  orangeGlow: 'rgba(255,16,38,0.5)',
  orangeSoft: 'rgba(255,16,38,0.15)',

  gold: '#f5f5f5',
  goldGlow: 'rgba(245,245,245,0.4)',

  enemy: '#ff2d2d',
  enemyGlow: 'rgba(255, 45, 45, 0.4)',

  win: '#00ff88',
  winGlow: 'rgba(0, 255, 136, 0.4)',
  lose: '#ff3366',
  loseGlow: 'rgba(255, 51, 102, 0.4)',

  text: '#ffffff',
  textDim: '#b0b0b0',
  textMuted: '#666666',

  border: 'rgba(255,16,38,0.3)',
  borderDim: 'rgba(255, 255, 255, 0.08)',
};

const injectKeyframes = () => {
  if (typeof document === 'undefined' || document.getElementById('fs-intel-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'fs-intel-keyframes';
  style.textContent = `
    @keyframes fs-pulse-glow { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
    @keyframes fs-scan-line { 0% { top: -10%; } 100% { top: 110%; } }
    @keyframes fs-data-flow { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
    @keyframes fs-radar-sweep { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  `;
  document.head.appendChild(style);
};

const DEFAULT_DATA = {
  name: 'Unknown',
  role: 'Unknown',
  position: '',
  pickRate: 0,
  banRate: 0,
  winRate: 50,
  totalGames: 0,
  avgKills: 0,
  avgDeaths: 0,
  avgAssists: 0,
  avgGPM: 0,
  avgParticipation: 0,
  radarStats: [
    { label: 'DMG', value: 50 },
    { label: 'Survive', value: 50 },
    { label: 'Gold', value: 50 },
    { label: 'Team', value: 50 },
    { label: 'Early', value: 50 },
  ],
  threatLevel: 50,
  vsWinRate: 50,
  recentForm: ['W', 'L', 'W', 'L', 'W'],
  comparison: {
    ourPick: 0,
    theirPick: 0,
    ourWR: 50,
    theirWR: 50,
    ourGPM: 650,
    theirGPM: 620,
  },
};

export function AnimatedNumber({ value, decimals = 0, suffix = '', duration = 1000 }) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);

  useEffect(() => {
    const target = Number.isFinite(Number(value)) ? Number(value) : 0;
    const start = prev.current;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setDisplay(start + (target - start) * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) requestAnimationFrame(animate);
      else prev.current = target;
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return (
    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
      {display.toFixed(decimals)}
      {suffix}
    </span>
  );
}

export function ProgressRing({ value, max = 100, size = 100, strokeWidth = 8, color, label = '' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  const percent = Math.min((safeValue / max) * 100, 100);
  const offset = circumference - (percent / 100) * circumference;
  const ringColor = color || (percent >= 50 ? COLORS.win : COLORS.lose);

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          style={{ filter: `drop-shadow(0 0 10px ${ringColor})` }}
        />
      </svg>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            fontSize: size * 0.22,
            fontWeight: 900,
            color: ringColor,
            fontFamily: "'Orbitron', sans-serif",
            textShadow: `0 0 20px ${ringColor}`,
          }}
        >
          <AnimatedNumber value={percent} decimals={1} suffix="%" />
        </span>
        {label && <span style={{ fontSize: 9, color: COLORS.textMuted, textTransform: 'uppercase' }}>{label}</span>}
      </div>
    </div>
  );
}

export function StatBar({ value, max = 100, label, color = COLORS.orange, unit = '' }) {
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  const percent = Math.min((safeValue / max) * 100, 100);

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span
          style={{
            fontSize: 11,
            color: COLORS.textDim,
            textTransform: 'uppercase',
            letterSpacing: 1,
            fontWeight: 600,
          }}
        >
          {label}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color, fontFamily: "'Orbitron', sans-serif" }}>
          <AnimatedNumber value={safeValue} decimals={1} suffix={unit} />
        </span>
      </div>

      <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.8 }}
          style={{
            height: '100%',
            background: `linear-gradient(90deg, ${color}, ${color}88)`,
            borderRadius: 4,
            boxShadow: `0 0 15px ${color}66`,
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'fs-data-flow 2s linear infinite',
            }}
          />
        </motion.div>
      </div>
    </div>
  );
}

export function KDADisplay({ kills, deaths, assists, size = 'normal' }) {
  const k = Number.isFinite(Number(kills)) ? Number(kills) : 0;
  const d = Number.isFinite(Number(deaths)) ? Number(deaths) : 0;
  const a = Number.isFinite(Number(assists)) ? Number(assists) : 0;

  const kda = d > 0 ? ((k + a) / d).toFixed(2) : 'Perfect';
  const fs = size === 'large' ? 32 : 20;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size === 'large' ? 12 : 8 }}>
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontSize: fs,
            fontWeight: 900,
            color: COLORS.win,
            fontFamily: "'Orbitron', sans-serif",
            textShadow: `0 0 20px ${COLORS.winGlow}`,
          }}
        >
          <AnimatedNumber value={k} decimals={1} />
        </div>
        <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase' }}>Kills</div>
      </div>

      <span style={{ fontSize: size === 'large' ? 28 : 16, color: COLORS.textMuted }}>/</span>

      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontSize: fs,
            fontWeight: 900,
            color: COLORS.lose,
            fontFamily: "'Orbitron', sans-serif",
            textShadow: `0 0 20px ${COLORS.loseGlow}`,
          }}
        >
          <AnimatedNumber value={d} decimals={1} />
        </div>
        <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase' }}>Deaths</div>
      </div>

      <span style={{ fontSize: size === 'large' ? 28 : 16, color: COLORS.textMuted }}>/</span>

      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontSize: fs,
            fontWeight: 900,
            color: COLORS.orange,
            fontFamily: "'Orbitron', sans-serif",
            textShadow: `0 0 20px ${COLORS.orangeGlow}`,
          }}
        >
          <AnimatedNumber value={a} decimals={1} />
        </div>
        <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase' }}>Assists</div>
      </div>

      <div
        style={{
          marginLeft: 16,
          padding: '10px 18px',
          background: COLORS.orangeSoft,
          borderRadius: 8,
          border: `2px solid ${COLORS.orange}`,
          boxShadow: `0 0 20px ${COLORS.orangeGlow}`,
        }}
      >
        <div style={{ fontSize: size === 'large' ? 22 : 16, fontWeight: 900, color: COLORS.orange, fontFamily: "'Orbitron', sans-serif" }}>
          {kda}
        </div>
        <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: 'uppercase' }}>KDA</div>
      </div>
    </div>
  );
}

export function RadarChart({ stats, size = 180, perspective = 'us' }) {
  const safeStats = Array.isArray(stats) && stats.length ? stats : DEFAULT_DATA.radarStats;

  const center = size / 2;
  const maxRadius = size * 0.30;
  const angles = safeStats.map((_, i) => (i * 2 * Math.PI) / safeStats.length - Math.PI / 2);
  const color = perspective === 'us' ? COLORS.orange : COLORS.enemy;

  const getPoint = (v, idx) => ({
    x: center + (v / 100) * maxRadius * Math.cos(angles[idx]),
    y: center + (v / 100) * maxRadius * Math.sin(angles[idx]),
  });

  const pts = safeStats.map((s, i) => getPoint(Number(s?.value) || 0, i));
  const path = pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ') + ' Z';

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        {[25, 50, 75, 100].map((r) => (
          <polygon
            key={r}
            points={angles
              .map((a) => `${center + (r / 100) * maxRadius * Math.cos(a)},${center + (r / 100) * maxRadius * Math.sin(a)}`)
              .join(' ')}
            fill="none"
            stroke={`${color}22`}
            strokeWidth="1"
          />
        ))}

        {angles.map((a, i) => (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={center + maxRadius * Math.cos(a)}
            y2={center + maxRadius * Math.sin(a)}
            stroke={`${color}33`}
            strokeWidth="1"
          />
        ))}

        <motion.path
          d={path}
          fill={`${color}22`}
          stroke={color}
          strokeWidth="2"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          style={{ transformOrigin: 'center', filter: `drop-shadow(0 0 10px ${color})` }}
        />

        {pts.map((p, i) => (
          <motion.circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="5"
            fill={color}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3 + i * 0.1 }}
            style={{ filter: `drop-shadow(0 0 8px ${color})` }}
          />
        ))}
      </svg>

      {safeStats.map((stat, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: center + (maxRadius + 28) * Math.cos(angles[i]),
            top: center + (maxRadius + 28) * Math.sin(angles[i]),
            transform: 'translate(-50%, -50%)',
            fontSize: 11,
            color: COLORS.textDim,
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          {stat?.label || ''}
        </div>
      ))}

      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: '50%', pointerEvents: 'none' }}>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '50%',
            height: 2,
            background: `linear-gradient(90deg, transparent, ${color})`,
            transformOrigin: 'left center',
            animation: 'fs-radar-sweep 4s linear infinite',
          }}
        />
      </div>
    </div>
  );
}

export function ThreatLevel({ level = 0 }) {
  const percent = Math.min(Number(level) || 0, 100);
  const color = percent >= 70 ? COLORS.lose : percent >= 40 ? COLORS.gold : COLORS.win;

  return (
    <div
      style={{
        padding: 18,
        background: `linear-gradient(135deg, ${COLORS.lose}15, transparent)`,
        borderRadius: 12,
        border: `2px solid ${COLORS.lose}66`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at 50% 50%, ${COLORS.lose}11, transparent)`,
          animation: 'fs-pulse-glow 2s ease-in-out infinite',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div
            style={{
              width: 32,
              height: 32,
              background: COLORS.enemy,
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              boxShadow: `0 0 15px ${COLORS.enemyGlow}`,
            }}
          >
            ⚠️
          </div>
          <span style={{ fontSize: 12, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700 }}>
            Threat Level
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 40, fontWeight: 900, color, fontFamily: "'Orbitron', sans-serif", textShadow: `0 0 30px ${color}` }}>
            <AnimatedNumber value={percent} decimals={0} />
          </span>
          <span style={{ fontSize: 16, color: COLORS.textMuted }}>/ 100</span>
        </div>

        <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
          {[...Array(10)].map((_, i) => {
            const active = i < Math.ceil(percent / 10);
            const segColor = i >= 7 ? COLORS.lose : i >= 4 ? COLORS.gold : COLORS.win;
            return (
              <motion.div
                key={i}
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: i * 0.05 }}
                style={{
                  flex: 1,
                  height: 12,
                  background: active ? segColor : 'rgba(255,255,255,0.06)',
                  borderRadius: 3,
                  boxShadow: active ? `0 0 10px ${segColor}66` : 'none',
                  transformOrigin: 'bottom',
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function MetaBadge({ pickRate, banRate }) {
  const p = Number.isFinite(Number(pickRate)) ? Number(pickRate) : 0;
  const b = Number.isFinite(Number(banRate)) ? Number(banRate) : 0;

  const hot = p + b > 40;
  const contested = b > 30;

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <div
        style={{
          padding: '5px 12px',
          background: COLORS.orangeSoft,
          border: `1px solid ${COLORS.orange}`,
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 700,
          color: COLORS.orangeLight,
          textTransform: 'uppercase',
        }}
      >
        📊 Pick {p.toFixed(1)}%
      </div>

      <div
        style={{
          padding: '5px 12px',
          background: `${COLORS.enemy}22`,
          border: `1px solid ${COLORS.enemy}`,
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 700,
          color: COLORS.enemy,
          textTransform: 'uppercase',
        }}
      >
        🚫 Ban {b.toFixed(1)}%
      </div>

      {hot && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          style={{
            padding: '5px 12px',
            background: `linear-gradient(135deg, ${COLORS.orange}, ${COLORS.gold})`,
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 900,
            color: COLORS.bgDark,
            boxShadow: `0 0 15px ${COLORS.orangeGlow}`,
            textTransform: 'uppercase',
          }}
        >
          🔥 META HOT
        </motion.div>
      )}

      {contested && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          style={{
            padding: '5px 12px',
            background: `${COLORS.lose}22`,
            border: `1px solid ${COLORS.lose}`,
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 900,
            color: COLORS.lose,
            textTransform: 'uppercase',
          }}
        >
          ⚔️ CONTESTED
        </motion.div>
      )}
    </div>
  );
}

export function WinLossStreak({ history = [] }) {
  const safe = Array.isArray(history) ? history : [];
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: COLORS.textMuted,
          textTransform: 'uppercase',
          marginBottom: 10,
          letterSpacing: 2,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        Recent Form
        <div style={{ flex: 1, height: 1, background: COLORS.borderDim }} />
      </div>

      <div style={{ display: 'flex', gap: 5 }}>
        {safe.slice(-10).map((r, i) => (
          <motion.div
            key={i}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: i * 0.05 }}
            style={{
              width: 30,
              height: 30,
              borderRadius: 4,
              background: r === 'W' ? `${COLORS.win}22` : `${COLORS.lose}22`,
              border: `2px solid ${r === 'W' ? COLORS.win : COLORS.lose}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 900,
              color: r === 'W' ? COLORS.win : COLORS.lose,
              fontFamily: "'Orbitron', sans-serif",
              boxShadow: `0 0 10px ${r === 'W' ? COLORS.winGlow : COLORS.loseGlow}`,
            }}
          >
            {r}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function ComparisonBar({ label, usValue, themValue, maxValue }) {
  const u = Number.isFinite(Number(usValue)) ? Number(usValue) : 0;
  const t = Number.isFinite(Number(themValue)) ? Number(themValue) : 0;

  const max = maxValue || (Math.max(u, t) * 1.2) || 1;
  const usP = (u / max) * 100;
  const themP = (t / max) * 100;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, color: COLORS.textDim, textTransform: 'uppercase', marginBottom: 6, letterSpacing: 1, fontWeight: 600 }}>
        {label}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 5, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${usP}%` }}
            transition={{ duration: 0.8 }}
            style={{
              height: '100%',
              background: `linear-gradient(270deg, ${COLORS.orange}, ${COLORS.orangeDark})`,
              borderRadius: '5px 0 0 5px',
              boxShadow: `0 0 12px ${COLORS.orangeGlow}`,
            }}
          />
        </div>

        <div style={{ width: 3, height: 24, background: COLORS.textMuted, borderRadius: 2 }} />

        <div style={{ flex: 1, height: 10, background: 'rgba(255,255,255,0.06)', borderRadius: 5, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${themP}%` }}
            transition={{ duration: 0.8 }}
            style={{
              height: '100%',
              background: `linear-gradient(90deg, ${COLORS.enemy}, #ff6b6b)`,
              borderRadius: '0 5px 5px 0',
              boxShadow: `0 0 12px ${COLORS.enemyGlow}`,
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.orange, fontFamily: "'Orbitron', sans-serif" }}>
          {u.toFixed(1)}
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.enemy, fontFamily: "'Orbitron', sans-serif" }}>
          {t.toFixed(1)}
        </span>
      </div>
    </div>
  );
}

export function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: COLORS.textMuted,
        textTransform: 'uppercase',
        letterSpacing: 2,
        marginBottom: 10,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {children}
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${COLORS.borderDim}, transparent)` }} />
    </div>
  );
}

export function CompactIntelCard({ data, heroImage, perspective = 'us' }) {
  const safe = data || DEFAULT_DATA;

  const color = perspective === 'us' ? COLORS.orange : COLORS.enemy;
  const glow = perspective === 'us' ? COLORS.orangeGlow : COLORS.enemyGlow;

  useEffect(() => {
    injectKeyframes();
  }, []);

  if (!safe) {
    return (
      <div style={{ width: 300, padding: 14, background: COLORS.bgCard, borderRadius: 12, border: `1px solid ${COLORS.borderDim}`, color: COLORS.textDim }}>
        Loading...
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{
        width: 300,
        background: COLORS.bgCard,
        borderRadius: 12,
        border: `2px solid ${color}`,
        boxShadow: `0 15px 50px rgba(0,0,0,0.6), 0 0 30px ${glow}`,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: 14, background: `linear-gradient(135deg, ${perspective === 'us' ? COLORS.orangeSoft : `${COLORS.enemy}15`}, transparent)`, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 52, height: 52, borderRadius: 8, overflow: 'hidden', border: `2px solid ${color}`, boxShadow: `0 0 15px ${glow}`, flexShrink: 0 }}>
          {heroImage ? (
            <img src={heroImage} alt={safe.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: COLORS.bgDark, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
              🎮
            </div>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 900, color: COLORS.text, fontFamily: "'Orbitron', sans-serif" }}>{safe.name}</div>
          <div style={{ fontSize: 12, color: COLORS.textDim }}>
            {safe.role}
            {safe.position ? ` • ${safe.position}` : ''}
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 900,
              color: safe.winRate >= 50 ? COLORS.win : COLORS.lose,
              fontFamily: "'Orbitron', sans-serif",
              textShadow: `0 0 15px ${safe.winRate >= 50 ? COLORS.winGlow : COLORS.loseGlow}`,
            }}
          >
            {Number(safe.winRate).toFixed(1)}%
          </div>
          <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: 'uppercase' }}>WIN RATE</div>
        </div>
      </div>

      <div style={{ padding: 14, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, background: 'rgba(0,0,0,0.3)' }}>
        {[
          { l: 'PICK', v: safe.pickRate, c: COLORS.orange, fmt: (x) => `${Number(x).toFixed(1)}%` },
          { l: 'BAN', v: safe.banRate, c: COLORS.enemy, fmt: (x) => `${Number(x).toFixed(1)}%` },
          { l: 'GAMES', v: safe.totalGames, c: COLORS.gold, fmt: (x) => `${x}` },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: 'center', padding: '10px 6px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, border: `1px solid ${COLORS.borderDim}` }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: s.c, fontFamily: "'Orbitron', sans-serif" }}>{s.fmt(s.v)}</div>
            <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
          </div>
        ))}
      </div>

      <div style={{ padding: '10px 14px', borderTop: `1px solid ${COLORS.borderDim}`, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, background: 'rgba(0,0,0,0.2)' }}>
        <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.win }}>{Number(safe.avgKills || 0).toFixed(1)}</span>
        <span style={{ color: COLORS.textMuted }}>/</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.lose }}>{Number(safe.avgDeaths || 0).toFixed(1)}</span>
        <span style={{ color: COLORS.textMuted }}>/</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.orange }}>{Number(safe.avgAssists || 0).toFixed(1)}</span>
        <span style={{ color: COLORS.textMuted, fontSize: 11, marginLeft: 8, fontWeight: 600 }}>Avg KDA</span>
      </div>

      {safe.comboInsight && (
        <div
          style={{
            padding: '9px 14px',
            borderTop: `1px solid ${COLORS.borderDim}`,
            background: 'rgba(255,16,38,0.08)',
            color: COLORS.text,
            fontSize: 11,
            lineHeight: 1.35,
          }}
        >
          <div style={{ fontWeight: 900, color: COLORS.gold, marginBottom: 2 }}>COMBO ACTIVE</div>
          <div style={{ color: COLORS.textDim }}>{safe.comboInsight.combo}</div>
          <div style={{ color: COLORS.text }}>
            W {safe.comboInsight.win}/{safe.comboInsight.total} • WR {Number(safe.comboInsight.winRate || 0).toFixed(1)}%
          </div>
        </div>
      )}

      {Number(safe.threatLevel || 0) > 60 && (
        <div style={{ padding: '8px 14px', background: `linear-gradient(90deg, ${COLORS.lose}22, ${COLORS.lose}11)`, borderTop: `1px solid ${COLORS.enemy}`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, fontWeight: 700, color: COLORS.lose, textTransform: 'uppercase' }}>
          ⚠️ HIGH THREAT ({safe.threatLevel}/100)
        </div>
      )}
    </motion.div>
  );
}

/* ========== MAIN COMPONENT ========== */
export default function HeroIntelPanel({
  hero,
  heroImage,
  perspective = 'us',
  stats = {},
  intel = null,
  opponent = '',
  onClose,
  compact = false,
  style = {},
}) {
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    injectKeyframes();
  }, []);

  const data = useMemo(() => {
    // ✅ FIX: heroName ต้อง fallback ได้จาก stats ด้วย
    const heroName =
      hero?.name ||
      hero ||
      stats?.name ||
      stats?.hero ||
      null;

    // ✅ ถ้าไม่มี heroName จริง ๆ ให้ return DEFAULT_DATA กันพังทุกกรณี
    if (!heroName) return { ...DEFAULT_DATA };

    const fsData = intel?.fs?.[heroName];
    const oppData = opponent ? intel?.oppByTeam?.[opponent]?.[heroName] : null;
    const globalData = intel?.global?.[heroName];

    const totalGames = (stats.totalGames ?? fsData?.pick ?? globalData?.pick ?? 0);
    const totalForRate = (intel?.totalGames ?? 127);

    const rawPickRate =
      stats.pickRate ??
      (totalForRate > 0 ? (totalGames / totalForRate) * 100 : 0);

    const rawBanRate =
      stats.banRate ??
      (totalForRate > 0
        ? (((fsData?.ban ?? globalData?.ban ?? 0) / totalForRate) * 100)
        : 0);

    const pickRate = Number.isFinite(rawPickRate) ? rawPickRate : 0;
    const banRate = Number.isFinite(rawBanRate) ? rawBanRate : 0;

    const winRate = (stats.winRate ?? fsData?.winRate ?? globalData?.winRate ?? 50);

    return {
      ...DEFAULT_DATA,
      name: heroName,
      role: hero?.role || stats.role || 'Unknown',
      position: stats.position || '',

      pickRate,
      banRate,
      winRate,
      totalGames,

      avgKills: stats.avgKills ?? 0,
      avgDeaths: stats.avgDeaths ?? 0,
      avgAssists: stats.avgAssists ?? 0,
      avgGPM: stats.avgGPM ?? 0,
      avgParticipation: stats.avgParticipation ?? 0,

      radarStats: stats.radarStats || DEFAULT_DATA.radarStats,

      threatLevel: stats.threatLevel ?? 50,
      vsWinRate: stats.vsWinRate ?? (100 - winRate),

      recentForm:
        stats.recentForm ||
        ['W', 'W', 'L', 'W', 'L', 'W', 'W', 'W', 'L', 'W'].slice(0, Math.min(10, totalGames || 5)),

      comparison: stats.comparison || {
        ourPick: fsData?.pick || 0,
        theirPick: oppData?.pick || 0,
        ourWR: fsData?.winRate || 50,
        theirWR: oppData?.winRate || 50,
        ourGPM: stats.avgGPM || 650,
        theirGPM: 620,
      },
    };
  }, [hero, stats, intel, opponent]);

  if (compact) return <CompactIntelCard data={data} heroImage={heroImage} perspective={perspective} />;

  const accentColor = perspective === 'us' ? COLORS.orange : COLORS.enemy;
  const accentGlow = perspective === 'us' ? COLORS.orangeGlow : COLORS.enemyGlow;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'performance', label: 'Performance', icon: '⚡' },
    { id: 'matchup', label: 'Matchup', icon: '⚔️' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      style={{
        width: 420,
        background: COLORS.bgCard,
        borderRadius: 16,
        border: `1px solid ${perspective === 'us' ? COLORS.border : `${COLORS.enemy}44`}`,
        boxShadow: `0 20px 60px rgba(0,0,0,0.8), 0 0 40px ${accentGlow}`,
        overflow: 'hidden',
        position: 'relative',
        ...style,
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, width: 50, height: 50, borderTop: `3px solid ${accentColor}`, borderLeft: `3px solid ${accentColor}`, borderRadius: '16px 0 0 0', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: 0, right: 0, width: 50, height: 50, borderBottom: `3px solid ${accentColor}`, borderRight: `3px solid ${accentColor}`, borderRadius: '0 0 16px 0', pointerEvents: 'none' }} />

      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 10 }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`, animation: 'fs-scan-line 3s linear infinite' }} />
      </div>

      <div
        style={{
          padding: 20,
          background: `linear-gradient(135deg, ${perspective === 'us' ? COLORS.orangeSoft : `${COLORS.enemy}15`}, transparent 70%)`,
          borderBottom: `1px solid ${COLORS.borderDim}`,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          position: 'relative',
        }}
      >
        <div style={{ width: 76, height: 76, borderRadius: 12, overflow: 'hidden', border: `3px solid ${accentColor}`, boxShadow: `0 0 25px ${accentGlow}`, flexShrink: 0 }}>
          {heroImage ? (
            <img src={heroImage} alt={data.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', background: COLORS.bgDark, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>
              🎮
            </div>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: COLORS.text, fontFamily: "'Orbitron', sans-serif" }}>{data.name}</h2>
            <span style={{ padding: '4px 12px', background: accentColor, borderRadius: 4, fontSize: 11, fontWeight: 900, color: COLORS.bgDark, textTransform: 'uppercase', boxShadow: `0 0 15px ${accentGlow}` }}>
              {perspective === 'us' ? 'FS' : 'Enemy'}
            </span>
          </div>

          <div style={{ fontSize: 14, color: COLORS.textDim, marginTop: 4 }}>
            {data.role}
            {data.position ? ` • ${data.position}` : ''}
          </div>

          <div style={{ marginTop: 10 }}>
            <MetaBadge pickRate={data.pickRate} banRate={data.banRate} />
          </div>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${COLORS.borderDim}`,
              background: 'rgba(255,255,255,0.05)',
              color: COLORS.textDim,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
            }}
          >
            ✕
          </button>
        )}
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.borderDim}`, background: 'rgba(0,0,0,0.3)' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: '14px 16px',
              background: activeTab === tab.id ? `${accentColor}15` : 'transparent',
              border: 'none',
              borderBottom: `3px solid ${activeTab === tab.id ? accentColor : 'transparent'}`,
              color: activeTab === tab.id ? accentColor : COLORS.textMuted,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              textTransform: 'uppercase',
              fontFamily: 'inherit',
            }}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: 24 }}>
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <div style={{ display: 'flex', gap: 24, marginBottom: 24 }}>
                <ProgressRing value={data.winRate} label="Win Rate" size={80} strokeWidth={10} />
                <div style={{ flex: 1 }}>
                  <StatBar label="Pick Rate" value={data.pickRate} max={30} color={COLORS.orange} unit="%" />
                  <StatBar label="Ban Rate" value={data.banRate} max={30} color={COLORS.enemy} unit="%" />
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 10, fontWeight: 600 }}>
                    Total Games: <span style={{ color: COLORS.orange, fontFamily: "'Orbitron', sans-serif" }}>{data.totalGames}</span>
                  </div>
                </div>
              </div>
              {data.comboInsight && (
                <div
                  style={{
                    marginBottom: 18,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${COLORS.border}`,
                    background: 'rgba(255,16,38,0.08)',
                  }}
                >
                  <div style={{ fontSize: 11, color: COLORS.gold, fontWeight: 900, letterSpacing: 1 }}>COMBO ACTIVE</div>
                  <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 700, marginTop: 4 }}>{data.comboInsight.combo}</div>
                  <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 2 }}>
                    W {data.comboInsight.win}/{data.comboInsight.total} • WR {Number(data.comboInsight.winRate || 0).toFixed(1)}%
                  </div>
                </div>
              )}
              <WinLossStreak history={data.recentForm} />
            </motion.div>
          )}

          {activeTab === 'performance' && (
            <motion.div key="performance" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <div style={{ marginBottom: 24 }}>
                <SectionLabel>Average KDA</SectionLabel>
                <KDADisplay kills={data.avgKills} deaths={data.avgDeaths} assists={data.avgAssists} size="large" />
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <RadarChart stats={data.radarStats} size={160} perspective={perspective} />
              </div>

              <StatBar label="Avg GPM" value={data.avgGPM} max={900} color={COLORS.gold} />
              <StatBar label="Participation" value={data.avgParticipation} max={100} color={COLORS.orange} unit="%" />
            </motion.div>
          )}

          {activeTab === 'matchup' && (
            <motion.div key="matchup" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
              <ThreatLevel level={data.threatLevel} />
              <div style={{ marginTop: 24 }}>
                <SectionLabel>{TEAM_SHORT} vs Enemy</SectionLabel>
                <ComparisonBar label="Pick Count" usValue={data.comparison.ourPick} themValue={data.comparison.theirPick} maxValue={20} />
                <ComparisonBar label="Win Rate %" usValue={data.comparison.ourWR} themValue={data.comparison.theirWR} maxValue={100} />
                <ComparisonBar label="Avg GPM" usValue={data.comparison.ourGPM} themValue={data.comparison.theirGPM} maxValue={800} />
              </div>

              <div style={{ marginTop: 20, padding: 18, background: COLORS.orangeSoft, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
                <SectionLabel>Win Rate vs This Hero</SectionLabel>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span
                    style={{
                      fontSize: 32,
                      fontWeight: 900,
                      color: data.vsWinRate >= 50 ? COLORS.win : COLORS.lose,
                      fontFamily: "'Orbitron', sans-serif",
                      textShadow: `0 0 20px ${data.vsWinRate >= 50 ? COLORS.winGlow : COLORS.loseGlow}`,
                    }}
                  >
                    <AnimatedNumber value={data.vsWinRate} decimals={1} suffix="%" />
                  </span>
                  {data.vsWinRate >= 50 ? (
                    <span style={{ color: COLORS.win, fontSize: 13, fontWeight: 700 }}>▲ Favorable</span>
                  ) : (
                    <span style={{ color: COLORS.lose, fontSize: 13, fontWeight: 700 }}>▼ Unfavorable</span>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
